import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Grid, Stack, TextField, Typography, Card, InputAdornment, IconButton, List, ListItem } from '@mui/material';
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
// icons
import { RemoveRedEyeRounded } from "@mui/icons-material";
import AngleRightIcon from "common/customIcons/angle_right";
import SearchIcon from 'common/customIcons/search';
// utils and helpers
import { getDistributionConfig, getDriverKit } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { openCustomModalDialog } from "../../../../redux/slices/modalSlice";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import { formatTimestampToWithoutTime } from "utilities/common";
import { debounce } from "utilities/common";
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";
import StatusText from "common/component/StatusText";
import StartArrowIIcon from 'common/customIcons/startArrowI';
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";

const DriverListPage = () => {
  const gridRef = useRef();
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { distributionId } = useParams()
  const [searchParams] = useSearchParams()
  const { region } = Object.fromEntries(searchParams.entries())

  const [details, setDetails] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [distributionConfigs, setDistributionConfigs] = useState({});

  const columnDefs = useMemo(() => {
    const openSummaryModal = (driverRow) => {
      dispatch(
        openCustomModalDialog({
          modal: 'DRIVER_KIT_TRANSACTION_SUMMARY',
          modalData: { driverRow },
        })
      );
    };

    const columns = [
      {
        headerName: 'Driver Name',
        field: 'driverName',
        suppressMenu: false,
        flex: 2,
        minWidth: 220,
        sortable: false,
      },
      {
        headerName: 'Sub Region',
        field: 'subRegion',
        suppressMenu: false,
        flex: 1.5,
        minWidth: 180,
        sortable: false,
      },
      {
        headerName: 'Kit ID',
        field: 'kitId',
        suppressMenu: false,
        flex: 1,
        minWidth: 120,
        sortable: false,
      },
      {
        headerName: 'Total No Of Products Expected/Scanned',
        // headerName: '<p>Total No of products <br /><span>Expected/Scanned</span></p>',
        field: 'totalExpected',
        suppressMenu: false,
        flex: 1.5,
        minWidth: 170,
        sortable: false,
        valueGetter: ({ data }) => `${data.totalExpected}/${data.totalScanned}`
      },
      {
        headerName: 'Kit Value Expected/Scanned',
        field: 'wholesaleExpected',
        suppressMenu: false,
        flex: 1.5,
        minWidth: 170,
        sortable: false,
        valueGetter: ({ data }) => `${data.wholesaleExpected}/${data.wholesaleScanned}`
      },
      {
        headerName: 'Discrepancies Count',
        field: 'discrepancyCount',
        suppressMenu: false,
        flex: 1.25,
        minWidth: 150,
        sortable: false,
      },
      {
        headerName: 'Status',
        field: 'status',
        suppressMenu: false,
        flex: 1.25,
        minWidth: 180,
        sortable: false,
        cellRenderer: ({ data }) => <StatusText status={data?.status} />
      },
      {
        headerName: 'Summary',
        field: 'summary',
        colId: 'summary',
        suppressMenu: false,
        flex: 1.25,
        minWidth: 200,
        sortable: false,
        cellRenderer: ({ data }) => (
          <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>
            <MDButton
              size="small"
              variant="contained"
              color="primary"
              startIcon={<RemoveRedEyeRounded fontSize="small" />}
              className="btn-primary-light"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => openSummaryModal(data)}
            >
              View Summary
            </MDButton>
          </Box>
        ),
      },
      {
        headerName: '',
        field: 'actions',
        suppressMenu: false,
        flex: 1,
        minWidth: 150,
        sortable: false,
        cellStyle: { textAlign: 'left', justifyContent: 'start' },
        cellRenderer: ({ data }) => {
          const { regionId, subRegionId, driverId, status } = data || {}
          if (!driverId) return null;

          return <Box>
            <MDButton
              onClick={() => {
                navigate(`/hyperwolf/driver-kit-verification/drivers/${distributionId}/details/${driverId}?regionId=${regionId}&subRegionId=${subRegionId}`)
              }}
              size='small'
              startIcon={["verified and dispatched", "dispatched", "on hold"].includes(status?.toLowerCase()) ? <RemoveRedEyeRounded fontSize='small' /> : <StartArrowIIcon />}
              variant='contained'
              color='primary'
              className='btn-primary-light'
              style={{ whiteSpace: "nowrap" }}
            >
              {["verified and dispatched", "dispatched", "on hold"].includes(status?.toLowerCase()) ? "View" : "Start Scan"}
            </MDButton>
          </Box>
        },
      },
    ];

    // if (distributionConfigs.isRetailer) {
    //   columns.splice(5, 0, {
    //     headerName: 'Retail Value Expected/Scanned',
    //     field: 'retailExpected',
    //     suppressMenu: false,
    //     flex: 1.5,
    //     minWidth: 170,
    //     sortable: false,
    //     valueGetter: ({ data }) => `${data.retailExpected}/${data.retailScanned}`
    //   })
    // }

    return columns;
  }, [distributionConfigs, dispatch])

  const handleGetConfigs = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      const data = response?.data;
      if (data) {
        setDistributionConfigs(data || {});
      }
    } catch (error) {
      console.log("error loading default value", error)
    }
  }

  const handleDriverKit = async (filters = {}) => {
    try {
      if (gridRef.current) {
        gridRef.current?.api?.showLoadingOverlay();
      }

      const response = await dispatch(getDriverKit({ distributionId, regionId: region, ...filters })).unwrap();
      const { data } = response || {};
      if (!data?.length) {
        gridRef.current?.api?.showNoRowsOverlay();
      } else {
        gridRef.current?.api?.hideOverlay();
      }
      if (data) {
        setDetails(data || []);
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    }
  };

  const debounceSearch = useCallback(debounce((event) => {
    handleDriverKit({ search: event.target.value })
  }, 1000), []);

  useEffect(() => {
    if (distributionId && region) {
      const isHoldPending = sessionStorage.getItem("hold_kit_pending");
      if (isHoldPending) {
        sessionStorage.removeItem("hold_kit_pending");
        setTimeout(() => {
          handleDriverKit()
        }, 1200);
      } else {
        handleDriverKit()
      }
      handleGetConfigs()
    }
  }, [distributionId, region])

  return (
    <CommonLayout>
      <Box p={4} className="cus-contents">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Driver Kit Verification
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2.5, boxShadow: "none" }}>
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2}>
            {/* Distribution details */}
            <Grid item xs={12} sm={8}>
              <Typography variant='h4' fontWeight="semibold">Driver List</Typography>
              <List sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Distribution:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details.type}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Region:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.regionName}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Date of Creation:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{formatTimestampToWithoutTime(details?.createdDate)}</Typography>
                </ListItem>
              </List>
            </Grid>

            {/* Search filter */}
            <Grid item xs={12} sm={4}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ sm: "center" }}
                justifyContent="flex-end"
              >
                <Box className="search-gray">
                  <TextField
                    className="custom-search-field"
                    placeholder="Search"
                    value={searchTerm || ""}
                    onChange={e => {
                      const { value } = e.target;
                      setSearchTerm(value);
                      debounceSearch(e);
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">
                        <IconButton size='small'>
                          <SearchIcon width='18px' height='18px' />
                        </IconButton>
                      </InputAdornment>
                    }}
                    style={{ minWidth: "300px" }}
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Drivers listing */}
        <Card sx={{ p: 0, borderRadius: 2.5, boxShadow: "none" }} className="header-ag-grid-lines table-nodata-size">
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
            <Grid item xs={12}>
              <div
                className="ag-theme-quartz"
                style={{ height: "70vh", width: "100%" }}
              >
                <AgGridReact
                  ref={gridRef}
                  className="ag-grid--table"
                  pagination={false}
                  rowData={details.drivers || null}
                  noRowsOverlayComponent={NoDataFound}
                  loadingOverlayComponent={TableLoader}
                  columnDefs={columnDefs}
                  rowModelType={"clientSide"}
                  headerHeight={36}
                  rowHeight={56}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  defaultColDef={{ ...defaultColDef }}
                  onCellClicked={(props) => {
                    const { regionId, subRegionId, driverId } = props?.data || {}

                    // Prevent row click if click originated from the actions column
                    if (props?.column && (["actions", "summary"].includes(props.column.getColId()))) {
                      return;
                    }
                    navigate(`/hyperwolf/driver-kit-verification/drivers/${distributionId}/details/${driverId}?regionId=${regionId}&subRegionId=${subRegionId}`)
                  }}
                />
              </div>
            </Grid>
          </Grid>
        </Card>

      </Box>
    </CommonLayout>
  )
}

export default DriverListPage
